import { NUMEO_CLIENT_HEADER, NumeoClient, type DirectReadCardName, type DirectWriteCardName, type QuickAnswerKind } from '@numeo/types'
import type { TmsWriteRow } from '@numeo/types-main-web-app'
import { isAxiosError } from 'axios'
import { generalAgentServiceAxios } from 'setup/SetupAxios'
import type { GeneralChatMessage } from '../types'

// The header selects the general agent (ADR-048) on general-agent-service, as the chat's own calls do.
const GENERAL_CLIENT_HEADERS = { [NUMEO_CLIENT_HEADER]: NumeoClient.General }

/** The approval question a write card asked: its text, the state to answer under, and the form it wants. */
export interface WriteQuestion {
    requestState: string
    message: string
    inputRequests: Record<string, unknown>
}

/** The user's answer to a question, sent with the `requestState` the question carried. `accept` needs `confirm: true`. */
export interface WriteApproval {
    requestState: string
    action: 'accept' | 'decline' | 'cancel'
    confirm?: boolean
}

/** The structured answer of a write card: `rows` on a write answer (one per load). Always an object; `{}` when the answer has none. */
export type WriteStructured = { rows?: TmsWriteRow[] } & Record<string, unknown>

/**
 * What a write card did: `answered` (its rows, refusals and confirm options are in `text` and `structured`), `question` (an approval
 * to answer, in `question`), or `refused` (the gateway refused the call).
 */
export interface WriteOutcome {
    status: 'answered' | 'question' | 'refused'
    text: string
    structured: WriteStructured
    question?: WriteQuestion
}

/** The route's answer as it comes over the wire: `structured` is null when the card answered with text only (and on a question). */
interface WireWriteOutcome extends Omit<WriteOutcome, 'structured'> {
    structured: WriteStructured | null
}

/** The three messages a quick answer stores: the suggestion's words, the card's result, and the template reply. */
interface QuickAnswerResult {
    user: GeneralChatMessage
    reply: GeneralChatMessage
    result: GeneralChatMessage
}

/**
 * The write cards whose question a drawer's own button may answer (the approved risk tiers): an invoice email the email drawer shows
 * (tier 1, its Send button), a POD accept whose reason the review drawer asks for (tier 3), a factoring batch the factoring drawer
 * lists ("Approve & prepare batch", nothing is sent to the factor), and the invoice draft and typed rate the review drawer shows
 * ("Approve & create draft"). Create and a clean accept run unasked inside the gateway's per-minute budget; past it they ask, and the
 * button that showed their facts answers, so a fourth load in a minute is not reported as refused. Submitting a batch to the factor
 * is never answered here: it is not a direct card, and it stays the chat's own approval card. Any other card's question is returned
 * to the caller, `approvedInDrawer` or not.
 */
export const DRAWER_APPROVED_CARDS: ReadonlySet<DirectWriteCardName> = new Set<DirectWriteCardName>([
    'tms_invoices.send',
    'tms_invoices.create',
    'tms_loads.edit',
    'tms_load_documents.accept',
    'tms_factoring.prepare_batch',
])

/**
 * How `approveWrite` runs. `approvedInDrawer` says the caller's own control showed the question's content and is the approval: only
 * then, and only for a card in `DRAWER_APPROVED_CARDS`, is the question answered `accept`. Any other question is returned as it is,
 * with its words as the outcome's text, so nothing is confirmed that the user did not see. `decline` refuses the question instead,
 * and `onQuestion` sees the question before it is answered.
 */
interface ApproveOptions {
    approvedInDrawer?: boolean
    decline?: boolean
    onQuestion?: (question: WriteQuestion) => void
}

/** Runs a write card directly as the user: the gateway's checks, locks and approval question all apply. Nothing goes to the model. */
export async function runWriteCard(tabId: string, card: DirectWriteCardName, params: object, approval?: WriteApproval): Promise<WriteOutcome> {
    const response = await generalAgentServiceAxios.post<{ success: boolean; data: WireWriteOutcome }>(
        `/tabs/${encodeURIComponent(tabId)}/cards/write`,
        { card, params, ...(approval ? { approval } : {}) },
        { headers: GENERAL_CLIENT_HEADERS }
    )
    const outcome = response.data.data
    return { ...outcome, structured: outcome.structured ?? {} }
}

/** Runs a read card directly and returns its structured answer. A refusal (HTTP 422) rejects. */
export async function readCard<T>(tabId: string, card: DirectReadCardName, params: object): Promise<T> {
    const response = await generalAgentServiceAxios.post<{ success: boolean; data: { text: string; structured: T } }>(
        `/tabs/${encodeURIComponent(tabId)}/cards/read`,
        { card, params },
        { headers: GENERAL_CLIENT_HEADERS }
    )
    return response.data.data.structured
}

/** How long the one automatic retry of a quick answer waits: long enough for a service that is still starting to finish opening. */
export const QUICK_ANSWER_RETRY_MS = 1_500

/**
 * A failure the same request may outlive: no answer at all, or the service failing (5xx; on a cold start the gateway session is still
 * opening). A refusal (4xx: the read was refused, the chat is gone) is final.
 */
const isTransient = (error: unknown): boolean => isAxiosError(error) && (!error.response || error.response.status >= 500)

/**
 * Answers one of the three suggestions without the model: the chat stores the three messages, and the caller appends them. A transient
 * failure is retried once, so a first click while the services start is answered instead of failing: the route stores nothing until
 * its read succeeded, so a retry does not repeat the question.
 */
export async function runQuickAnswer(tabId: string, kind: QuickAnswerKind): Promise<QuickAnswerResult> {
    const post = () =>
        generalAgentServiceAxios.post<{ success: boolean; data: QuickAnswerResult }>(
            `/tabs/${encodeURIComponent(tabId)}/quick-answers`,
            { kind },
            { headers: GENERAL_CLIENT_HEADERS }
        )
    try {
        return (await post()).data.data
    } catch (error) {
        if (!isTransient(error)) throw error
        await new Promise((resolve) => setTimeout(resolve, QUICK_ANSWER_RETRY_MS))
        return (await post()).data.data
    }
}

/**
 * The approval handshake of a direct write. Runs the card; a question it asks is answered only when the caller's control showed it
 * (`approvedInDrawer`: accept with `confirm: true`) or to refuse it (`decline`), under the `requestState` the question carried. Any
 * other question stops here and is returned with its words, for the caller to show. At most two calls: a second question is returned
 * as it is.
 */
export async function approveWrite(tabId: string, card: DirectWriteCardName, params: object, opts: ApproveOptions = {}): Promise<WriteOutcome> {
    const first = await runWriteCard(tabId, card, params)
    if (first.status !== 'question' || !first.question) return first
    opts.onQuestion?.(first.question)
    const { requestState } = first.question
    if (opts.decline) return runWriteCard(tabId, card, params, { requestState, action: 'decline' })
    if (!opts.approvedInDrawer || !DRAWER_APPROVED_CARDS.has(card)) return { ...first, text: first.text || first.question.message }
    return runWriteCard(tabId, card, params, { requestState, action: 'accept', confirm: true })
}
